// =============================================================================
// Optim TURPE V2 — BORNES ET CONTRÔLES servis par le moteur (DEV-1913).
//
// Ce que le client doit vivre : sur la ligne de la puissance de raccordement, il
// lit ce que nous en avons fait — appliquée, levée par lui, écartée parce que ses
// appels mesurés la dépassent, ou inconnue. Et sur la ligne de chaque classe de
// son dénivelé actuel, il lit si la valeur est cohérente avec ce que le compteur
// a réellement appelé.
//
// Rien de tout cela n'est une bannière (règle de Mathieu) : chaque fait est rendu
// DANS la donnée concernée.
//
// Le repli : quand le moteur ne sert pas encore `restitution.bornes`, on lit la
// valeur brute du raccordement que le document porte déjà, et on dit que son
// statut n'est pas servi — au lieu d'affirmer « appliquée », ce qui serait faux
// (le champ était transmis aux optimiseurs et lu par aucun, constat F1.9).
// =============================================================================

import { LIBELLE_RACCORDEMENT_MOTIF } from "@modules/courtage/utils/optim-turpe-v2.libelles"
import { getUnitePuissance } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { uniteDuRaccordement } from "@modules/courtage/utils/optim-turpe-v2.saisie"
import type {
    OptimTurpeV2ControleDenivele,
    OptimTurpeV2Etude,
    OptimTurpeV2RaccordementStatut,
} from "@modules/courtage/types/optim-turpe-v2"

function fini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

export type RaccordementLu = {
    /** Statut servi par le moteur. `null` = statut non servi (étude antérieure au lot). */
    statut: OptimTurpeV2RaccordementStatut | null
    /** Motif d'un statut `ecartee`, servi par le moteur. */
    statutMotif: string | null
    /** Phrase du motif, proposition à valider. `null` si le motif est inconnu de la page. */
    statutMotifLibelle: string | null
    /** Valeur NORMALISÉE par le moteur, sinon la valeur brute du document. */
    valeur: number | null
    /**
     * Unité DU CHAMP (§4.4.2 point 3) — pas celle du segment. En C2 le moteur peut
     * servir une borne en kVA à côté de puissances souscrites en kW : l'écran
     * étiquette alors le raccordement en kVA, sous peine de mentir sur la grandeur.
     */
    unite: string | null
    valeurRecue: number | string | null
    uniteRecue: string | null
    /** La plus haute pointe mesurée de la fenêtre, qui justifie `appels_superieurs`. */
    pointeMesureeMax: number | null
    pointeMesureeUnite: string | null
    /** La recommandation retenue touche-t-elle la borne ? */
    recommandationBornee: boolean
    /** La borne est valable mais plus haute que la réalité du segment. */
    borneLache: boolean
    /** Vrai quand le bloc `restitution.bornes.raccordement` est servi. */
    servi: boolean
}

/**
 * La borne de raccordement telle que le moteur la sert, ou son repli honnête.
 *
 * Rend `null` seulement quand rien n'est connu : ni bloc servi, ni valeur brute.
 * La page n'affiche alors pas de ligne de raccordement vide.
 */
export function lireRaccordement(
    etude: OptimTurpeV2Etude
): RaccordementLu | null {
    const servi = etude.bornes?.raccordement ?? null
    if (servi) {
        const motif = servi.statut_motif ?? null
        return {
            statut: servi.statut ?? null,
            statutMotif: motif,
            statutMotifLibelle: motif
                ? (LIBELLE_RACCORDEMENT_MOTIF[motif] ?? null)
                : null,
            valeur: fini(servi.valeur),
            // R10 (08/10/2026) : l'unité servie, sinon celle du segment. Elle
            // n'est plus dite « présumée » : c'est l'unité du segment.
            unite: servi.unite ?? uniteDuRaccordement(etude.compteur?.segment),
            valeurRecue: servi.valeur_recue ?? null,
            uniteRecue: servi.unite_recue ?? null,
            pointeMesureeMax: fini(servi.pointe_mesuree_max),
            pointeMesureeUnite: servi.pointe_mesuree_unite ?? null,
            recommandationBornee: servi.recommandation_bornee === true,
            borneLache: servi.borne_lache === true,
            servi: true,
        }
    }

    // Repli : la valeur brute que le document porte déjà, sans son statut. On ne
    // prétend pas qu'elle borne quoi que ce soit — jusqu'au lot DEV-1909, aucun
    // optimiseur ne la lisait.
    const brute =
        fini(
            etude.inputs?.compteur?.raccordement
                ?.puissance_raccordement_soutirage
        ) ??
        fini(etude.compteur?.raccordement?.puissance_raccordement_soutirage)
    if (brute === null) return null
    const uniteSaisie =
        etude.inputs?.compteur?.raccordement
            ?.puissance_raccordement_soutirage_unite ?? null
    return {
        statut: null,
        statutMotif: null,
        statutMotifLibelle: null,
        valeur: brute,
        unite: uniteSaisie ?? uniteDuRaccordement(etude.compteur?.segment),
        valeurRecue: brute,
        uniteRecue: uniteSaisie,
        pointeMesureeMax: null,
        pointeMesureeUnite: null,
        recommandationBornee: false,
        borneLache: false,
        servi: false,
    }
}

export type ControleDeniveleLu = {
    classe: string
    souscrite: number | null
    pointeMesuree: number | null
    /**
     * La pointe qui s'impose à la classe (courtage#251, DEV-2171) : la plus
     * haute des classes qu'elle doit dominer, ou le plancher de segment C4.
     * `null` sur une étude antérieure, qui ne la sert pas.
     */
    referenceMesuree: number | null
    /** La classe qui porte cette pointe, ou `plancher-segment-c4`. */
    referenceClasse: string | null
    ecartPct: number | null
    /** Verdict servi. `null` = pas de mesure sur la classe, donc pas de verdict fabriqué. */
    verdict: OptimTurpeV2ControleDenivele["verdict"]
    unite: string
}

/**
 * Le contrôle de vraisemblance du dénivelé ACTUEL, une ligne par classe (§4.5).
 *
 * Rend `null` quand le moteur ne le sert pas : la page n'affiche alors aucun
 * verdict, plutôt qu'un verdict qu'elle aurait calculé elle-même. C'est ce
 * contrôle qui remplace l'ancien état `sur_segmente`, supprimé avec le plancher
 * de 250 kW (A6).
 */
export function lireControlesDenivele(
    etude: OptimTurpeV2Etude
): ControleDeniveleLu[] | null {
    const lignes = etude.controles?.denivele_actuel
    if (!Array.isArray(lignes) || lignes.length === 0) return null
    const uniteDefaut = getUnitePuissance(etude)
    return lignes
        .filter((ligne) => typeof ligne?.classe === "string")
        .map((ligne) => ({
            classe: String(ligne.classe),
            souscrite: fini(ligne.souscrite),
            pointeMesuree: fini(ligne.pointe_mesuree),
            referenceMesuree: fini(ligne.reference_mesuree),
            referenceClasse:
                typeof ligne.reference_classe === "string"
                    ? ligne.reference_classe
                    : null,
            ecartPct: fini(ligne.ecart_pct),
            verdict: ligne.verdict ?? null,
            unite: ligne.unite ?? uniteDefaut,
        }))
}

/** Le verdict servi pour une classe donnée, ou `null`. */
export function verdictDeLaClasse(
    controles: ControleDeniveleLu[] | null,
    classe: string
): ControleDeniveleLu | null {
    return controles?.find((ligne) => ligne.classe === classe) ?? null
}
